/* eslint-disable max-lines */
import { type Clarification, type TextInfo } from "@design-lab/entities/pinning";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { TextField } from "@design-lab/shared/ui/design-system/TextField";
import { processTextWithHexCodes } from "@design-lab/shared/ui/MarkdownContent";
import { type RefObject } from "react";
import { getFontColorValueMetadata } from "./fontColorValue";
import { useGoogleFont } from "./useGoogleFont";

const CLARIFICATION_CONTAINER_MAX_HEIGHT = 280;

type ClarificationResponse = {
  selected: string | null;
  custom: string;
  isTypingCustom: boolean;
};

/**
 * Gets the effective answer value for a clarification response.
 */
export function getClarificationAnswer(response: ClarificationResponse | undefined): string | null {
  if (!response) return null;
  if (response.isTypingCustom) {
    return response.custom?.trim() || null;
  }
  return response.selected || null;
}

type FontPreviewOptionProps = {
  textValue: string;
  fontFamily: string;
  color: string;
  optionLabel: string;
  optionValue: string;
  isSelected: boolean;
  onPress: () => void;
  isDisabled: boolean;
  previewType: "font" | "color";
  compact?: boolean;
};

function FontPreviewOption({
  textValue,
  fontFamily,
  color,
  optionLabel,
  optionValue,
  isSelected,
  onPress,
  isDisabled,
  previewType,
  compact = false
}: FontPreviewOptionProps) {
  const fontToLoad = previewType === "font" ? optionValue : fontFamily;
  useGoogleFont(fontToLoad);

  const displayColor = previewType === "color" ? optionValue : color;
  const displayFont = previewType === "font" ? optionValue : fontFamily;
  const secondaryLabel = previewType === "color" ? `${optionLabel} (${optionValue})` : optionLabel;

  return (
    <div
      data-testid={`pin-clarify-preview-${optionLabel}`}
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      onClick={isDisabled ? undefined : onPress}
      onKeyDown={e => {
        if (!isDisabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onPress();
        }
      }}
      className={`flex cursor-pointer flex-col items-center justify-center border ${
        compact ? "w-[140px] rounded-sm px-2 py-2" : "w-[180px] rounded-lg px-3 py-3"
      } ${
        isSelected
          ? "border-blue-500 bg-blue-50 hover:bg-blue-100"
          : "border-gray-300 bg-white hover:bg-gray-50"
      } ${isDisabled ? "cursor-not-allowed opacity-50" : ""}`}
    >
      <div
        className={`max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-center font-bold ${
          compact ? "px-1 text-sm" : "px-2 text-lg"
        }`}
        style={{ fontFamily: `"${displayFont}", sans-serif`, color: displayColor }}
      >
        {textValue}
      </div>
      <div
        className={`max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-center text-gray-500 ${
          compact ? "mt-0.5 px-1 text-[10px]" : "mt-1 px-2 text-xs"
        }`}
      >
        {secondaryLabel}
      </div>
    </div>
  );
}

type ClarificationItemProps = {
  clarification: Clarification;
  response: ClarificationResponse;
  onSelectOption: (value: string | null) => void;
  onToggleCustomInput: () => void;
  onCustomInputChange: (value: string) => void;
  isDisabled: boolean;
  textInfo?: TextInfo | undefined;
  selectedFont?: string | undefined;
  selectedColor?: string | undefined;
  onSubmit: () => void;
  compact?: boolean;
};

function ClarificationItem({
  clarification,
  response,
  onSelectOption,
  onToggleCustomInput,
  onCustomInputChange,
  isDisabled,
  textInfo,
  selectedFont,
  selectedColor,
  onSubmit,
  compact = false
}: ClarificationItemProps) {
  const isFontSelection = clarification.type === "font_selection";
  const isFontColor = clarification.type === "font_color";
  const isColorShade = clarification.type === "color_shade";

  const textValue = textInfo?.value || clarification.label || "Sample";
  const baseFont = textInfo?.fontFamily || "inherit";
  const baseColor = textInfo?.color || "#000000";
  const effectiveFont = selectedFont || baseFont;
  const effectiveColor = selectedColor || baseColor;

  return (
    <div
      className={`flex flex-col ${compact ? "gap-1.5" : "gap-2"}`}
      data-clarification-label={clarification.label || clarification.question}
    >
      <div className={`text-sm text-gray-800`}>
        {processTextWithHexCodes(clarification.question)}
      </div>
      <div className={`flex flex-wrap ${compact ? "gap-1.5" : "gap-2"}`}>
        {clarification.options.map(opt => {
          const fontColorMetadata = isFontColor ? getFontColorValueMetadata(opt.value) : null;
          const canUseFontColorPreview =
            isFontColor && Boolean(fontColorMetadata?.singlePreviewHex);
          const canUseFontPreview = isFontSelection || canUseFontColorPreview;
          const displayText =
            (isColorShade || isFontColor) && opt.value ? `${opt.label} (${opt.value})` : opt.label;
          const isSelected = !response.isTypingCustom && response.selected === displayText;

          if (canUseFontPreview) {
            return (
              <FontPreviewOption
                key={`${opt.label}-${opt.value}`}
                textValue={textValue}
                fontFamily={isFontSelection ? opt.value || opt.label : effectiveFont}
                color={
                  isFontColor
                    ? fontColorMetadata?.singlePreviewHex || effectiveColor
                    : effectiveColor
                }
                optionLabel={opt.label}
                optionValue={opt.value || opt.label}
                isSelected={isSelected}
                onPress={() => onSelectOption(isSelected ? null : displayText)}
                isDisabled={isDisabled}
                previewType={isFontSelection ? "font" : "color"}
                compact={compact}
              />
            );
          }

          return (
            <div
              key={`${opt.label}-${opt.value}`}
              data-testid={`pin-clarify-${opt.label}`}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              onClick={
                isDisabled ? undefined : () => onSelectOption(isSelected ? null : displayText)
              }
              onKeyDown={e => {
                if (!isDisabled && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  onSelectOption(isSelected ? null : displayText);
                }
              }}
              className={`cursor-pointer border font-medium ${
                compact ? "rounded-sm px-2.5 py-1 text-xs" : "rounded-lg px-4 py-2 text-sm"
              } ${
                isSelected
                  ? "border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              } ${isDisabled ? "cursor-not-allowed opacity-50" : ""}`}
            >
              {processTextWithHexCodes(displayText)}
            </div>
          );
        })}
      </div>
      {response.isTypingCustom ? (
        <div className="flex items-center gap-2">
          <TextField
            value={response.custom}
            onChange={onCustomInputChange}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === "Return") {
                e.preventDefault();
                if (!isDisabled) {
                  onSubmit();
                }
              }
            }}
            placeholder="Type your answer..."
            className="flex-1"
            inputClassName="text-base focus:border-blue-500"
            isDisabled={isDisabled}
            autoFocus
            data-testid={`pin-clarify-custom-input-${clarification.type}`}
          />
          <Button
            variant="link"
            size="sm"
            onPress={onToggleCustomInput}
            isDisabled={isDisabled}
            className="text-gray-500 no-underline hover:text-gray-700 hover:no-underline"
            data-testid={`pin-clarify-cancel-custom-${clarification.type}`}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={isDisabled ? -1 : 0}
          onClick={isDisabled ? undefined : onToggleCustomInput}
          onKeyDown={e => {
            if (!isDisabled && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              onToggleCustomInput();
            }
          }}
          data-testid={`pin-clarify-type-own-${clarification.type}`}
          className={`cursor-pointer self-start border border-dashed border-gray-300 text-blue-600 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 ${
            compact ? "rounded-sm px-2.5 py-1 text-xs" : "rounded-lg px-4 py-2 text-sm"
          } ${isDisabled ? "cursor-not-allowed opacity-50" : ""}`}
        >
          + Type my own
        </div>
      )}
    </div>
  );
}

type PinClarificationsProps = {
  clarifications: Clarification[];
  responses: Record<string, ClarificationResponse>;
  onSelectOption: (question: string, value: string | null) => void;
  onToggleCustomInput: (question: string) => void;
  onCustomInputChange: (question: string, value: string) => void;
  onSubmit: () => void;
  isDisabled: boolean;
  textInfo?: TextInfo | undefined;
  selectedFont?: string | undefined;
  selectedColor?: string | undefined;
  scrollRef?: RefObject<HTMLDivElement | null>;
  onScroll?: () => void;
  unansweredAbove?: number;
  unansweredBelow?: number;
  onScrollToUnanswered?: (direction: "above" | "below") => void;
  compact?: boolean;
};

export function PinClarifications({
  clarifications,
  responses,
  onSelectOption,
  onToggleCustomInput,
  onCustomInputChange,
  onSubmit,
  isDisabled,
  textInfo,
  selectedFont,
  selectedColor,
  scrollRef,
  onScroll,
  unansweredAbove = 0,
  unansweredBelow = 0,
  onScrollToUnanswered,
  compact = false
}: PinClarificationsProps) {
  const answeredCount = clarifications.filter(c =>
    getClarificationAnswer(responses[c.question])
  ).length;

  return (
    <div
      className={`rounded-lg border border-gray-200 bg-gray-50 ${compact ? "p-1.5" : "p-3"} ${
        compact ? "mb-2" : "mb-4"
      }`}
    >
      <div
        className={`flex items-center justify-between text-gray-500 ${
          compact ? "mb-1 text-[10px]" : "mb-2 text-xs"
        }`}
      >
        <span>
          {clarifications.length} question{clarifications.length > 1 ? "s" : ""} to answer
        </span>
        <span>
          {answeredCount} of {clarifications.length} answered
        </span>
      </div>
      <div className="relative">
        <div
          ref={scrollRef}
          className={`flex flex-col overflow-y-auto pr-1 ${compact ? "gap-2" : "gap-4"}`}
          style={{ maxHeight: CLARIFICATION_CONTAINER_MAX_HEIGHT }}
          onScroll={onScroll}
        >
          {clarifications.map(clarification => (
            <ClarificationItem
              key={clarification.question}
              clarification={clarification}
              response={
                responses[clarification.question] ?? {
                  selected: null,
                  custom: "",
                  isTypingCustom: false
                }
              }
              onSelectOption={value => onSelectOption(clarification.question, value)}
              onToggleCustomInput={() => onToggleCustomInput(clarification.question)}
              onCustomInputChange={value => onCustomInputChange(clarification.question, value)}
              onSubmit={onSubmit}
              isDisabled={isDisabled}
              textInfo={textInfo}
              selectedFont={selectedFont}
              selectedColor={selectedColor}
              compact={compact}
            />
          ))}
        </div>
        {unansweredAbove > 0 && onScrollToUnanswered && (
          <div
            role="button"
            tabIndex={0}
            aria-label={`Scroll to ${unansweredAbove} unanswered above`}
            onClick={() => onScrollToUnanswered("above")}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onScrollToUnanswered("above");
              }
            }}
            className={`absolute -top-1 left-1/2 z-10 -translate-x-1/2 cursor-pointer rounded-full bg-blue-500 font-medium text-white shadow-md hover:bg-blue-600 ${
              compact ? "px-2 py-0.5 text-[10px]" : "px-3 py-1 text-xs"
            }`}
          >
            ↑ {unansweredAbove} unanswered above
          </div>
        )}
        {unansweredBelow > 0 && onScrollToUnanswered && (
          <div
            role="button"
            tabIndex={0}
            aria-label={`Scroll to ${unansweredBelow} unanswered below`}
            onClick={() => onScrollToUnanswered("below")}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onScrollToUnanswered("below");
              }
            }}
            className={`absolute -bottom-1 left-1/2 z-10 -translate-x-1/2 cursor-pointer rounded-full bg-blue-500 font-medium text-white shadow-md hover:bg-blue-600 ${
              compact ? "px-2 py-0.5 text-[10px]" : "px-3 py-1 text-xs"
            }`}
          >
            {unansweredBelow} unanswered below ↓
          </div>
        )}
      </div>
    </div>
  );
}
